import { GamesScreen, type GamesScreenWaitingGame } from '@words-happen/screens';
import type React from 'react';
import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { createGamesApi, type GamesApi, type GamesList } from '../../api/games';
import type { PageVisibility } from '../../games/page-visibility';
import { useMyGames } from '../../games/use-my-games';
import { gamePath, ROUTES } from '../../routes';
import { useSession } from '../../session';
import { useLobbyFailure } from './use-lobby-failure';

export interface GamesPageProps {
  /** Replaces words-happen-gql's lobby API. */
  gamesApi?: GamesApi;
  /** Replaces the 30-second poll. */
  pollMs?: number;
  /** Replaces the browser's page visibility. */
  visibility?: PageVisibility;
}

const EMPTY: GamesList = { active: [], waiting: [], finished: [] };

const GamesListView: React.FC<Required<Pick<GamesPageProps, 'gamesApi'>> & GamesPageProps> = ({
  gamesApi,
  pollMs,
  visibility,
}) => {
  const navigate = useNavigate();
  const handleFailure = useLobbyFailure();
  const { status, games = EMPTY, refresh } = useMyGames(gamesApi, { pollMs, visibility });
  const [cancelling, setCancelling] = useState<ReadonlySet<string>>(new Set());
  const [notice, setNotice] = useState<string>();

  const cancel = async (gameId: string) => {
    setNotice(undefined);
    setCancelling((ids) => new Set(ids).add(gameId));
    try {
      await gamesApi.cancelGame(gameId);
    } catch (error) {
      setNotice(handleFailure(error, ROUTES.games));
    }
    setCancelling((ids) => new Set([...ids].filter((id) => id !== gameId)));
    refresh();
  };

  const waiting = games.waiting.map(
    ({ id, quickMatch }): GamesScreenWaitingGame => ({
      id,
      kind: quickMatch ? 'quick-match' : 'invite',
      onShareInvite: quickMatch ? undefined : () => navigate(`${ROUTES.newGame}?game=${encodeURIComponent(id)}`),
      onCancel: () => void cancel(id),
      cancelling: cancelling.has(id),
    }),
  );

  return (
    <GamesScreen
      status={status}
      onRetryLoad={refresh}
      active={games.active.map(({ id, opponentName, isMyTurn, myScore, opponentScore }) => ({
        id,
        opponentName: opponentName ?? '',
        isMyTurn,
        myScore,
        opponentScore,
        onSelect: () => navigate(gamePath(id)),
      }))}
      waiting={waiting}
      finished={games.finished.map(({ id, opponentName, result, myScore, opponentScore }) => ({
        id,
        opponentName: opponentName ?? '',
        result: result ?? 'drew',
        myScore,
        opponentScore,
        onSelect: () => navigate(gamePath(id)),
      }))}
      notice={notice}
      onNewGame={() => navigate(ROUTES.newGame)}
      onBack={() => navigate(ROUTES.dashboard)}
    />
  );
};

/**
 * The games list, /games (WH-155): words-happen-gql myGames, polled (useMyGames), with Cancel for a waiting game and
 * Share invite, which shows the invitation again on /games/new. An active or finished game opens /games/:id. Signed
 * out, it goes to /welcome.
 */
export const GamesPage: React.FC<GamesPageProps> = ({ gamesApi, ...options }) => {
  const { signedIn } = useSession();
  const [api] = useState(() => gamesApi ?? createGamesApi());

  return signedIn ? <GamesListView gamesApi={api} {...options} /> : <Navigate replace to={ROUTES.welcome} />;
};
